import type { FlowDefinition, FlowFieldDefinition, FlowNode } from '../types';
import { VASE_FLOW_FIELD_IDS, rebuildFlowConnections } from './flows';
import { resolveOrCreateSectionInsertionFlow } from './sectionInsertionFlow';

const PRODUCT_CARD_FLOW_FIELDS: FlowFieldDefinition[] = [
  {
    id: VASE_FLOW_FIELD_IDS.chosenProduct,
    label: 'Chosen Product',
    type: 'entity.product',
  },
  {
    id: VASE_FLOW_FIELD_IDS.chosenFinish,
    label: 'Chosen Finish',
    type: 'scalar.string',
  },
  {
    id: VASE_FLOW_FIELD_IDS.chosenColours,
    label: 'Chosen Colours',
    type: 'list.string',
    multiple: true,
  },
  {
    id: VASE_FLOW_FIELD_IDS.chosenCombination,
    label: 'Chosen Combination',
    type: 'entity.combination',
  },
];

function normalizeProductCardLabel(label: string | null | undefined) {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  return trimmed.length > 0 ? trimmed : 'Product Card';
}

export function getProductCardSelectFieldId(blockId: string) {
  return `product-select-${blockId}`;
}

export function getProductCardSelectionFieldId(blockId: string) {
  return `product-selection-${blockId}`;
}

function buildProductCardSelectField(blockId: string, label: string | null | undefined): FlowFieldDefinition {
  return {
    id: getProductCardSelectFieldId(blockId),
    label: `${normalizeProductCardLabel(label)} Selected`,
    type: 'event.trigger',
  };
}

function buildProductCardSelectionField(blockId: string, label: string | null | undefined): FlowFieldDefinition {
  return {
    id: getProductCardSelectionFieldId(blockId),
    label: `${normalizeProductCardLabel(label)} Selected Product`,
    type: 'context.selection',
  };
}

function upsertFields(fields: FlowFieldDefinition[], additions: FlowFieldDefinition[]) {
  const nextById = new Map(fields.map((field) => [field.id, field]));
  additions.forEach((field) => {
    if (!nextById.has(field.id)) {
      nextById.set(field.id, field);
    }
  });
  return Array.from(nextById.values());
}

function upsertNodes(nodes: FlowNode[], additions: FlowNode[]) {
  const nextByBlockId = new Map(nodes.map((node) => [node.blockId, node]));
  additions.forEach((node) => {
    const existing = nextByBlockId.get(node.blockId);
    if (!existing) {
      nextByBlockId.set(node.blockId, node);
      return;
    }

    const mergeBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      defaults: FlowNode['inputs'] | FlowNode['outputs']
    ) => {
      const next = [...current];
      defaults.forEach((binding) => {
        if (!next.some((entry) => entry.socketId === binding.socketId)) {
          next.push(binding);
        }
      });
      return next;
    };

    const replaceOutputBindings = (
      current: FlowNode['outputs'],
      defaults: FlowNode['outputs']
    ) => {
      const defaultSocketIds = new Set(defaults.map((binding) => binding.socketId));
      return [
        ...current.filter((binding) => !defaultSocketIds.has(binding.socketId)),
        ...defaults,
      ];
    };

    nextByBlockId.set(node.blockId, {
      ...existing,
      id: existing.id || node.id,
      blockType: node.blockType,
      config: { ...(node.config ?? {}), ...(existing.config ?? {}) },
      inputs: mergeBindings(existing.inputs, node.inputs),
      outputs: replaceOutputBindings(existing.outputs, node.outputs),
    });
  });
  return Array.from(nextByBlockId.values());
}

export function upsertProductCardFlowNode(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    blockId: string;
    label?: string | null;
    sections?: Array<{
      id: string;
      settings?: {
        canvas?: {
          blocks?: Array<{
            id: string;
            type: string;
          }>;
        };
      };
    }> | null;
  }
) {
  const { flows: nextFlows, flow: targetFlow } = resolveOrCreateSectionInsertionFlow(flows, {
    sectionId: args.sectionId,
    sections: args.sections,
  });

  if (!targetFlow) return nextFlows;

  const nextNode: FlowNode = {
    id: `flow-node-${args.blockId}`,
    blockId: args.blockId,
    blockType: 'product-card',
    inputs: [],
    outputs: [
      { socketId: 'product', fieldId: getProductCardSelectionFieldId(args.blockId) },
      { socketId: 'select', fieldId: getProductCardSelectFieldId(args.blockId) },
    ],
  };

  const updatedFlowBase: FlowDefinition = {
    ...targetFlow,
    fields: upsertFields(targetFlow.fields, [
      ...PRODUCT_CARD_FLOW_FIELDS,
      buildProductCardSelectionField(args.blockId, args.label),
      buildProductCardSelectField(args.blockId, args.label),
    ]),
    nodes: upsertNodes(targetFlow.nodes, [nextNode]),
  };

  const updatedFlow: FlowDefinition = {
    ...updatedFlowBase,
    connections: rebuildFlowConnections(updatedFlowBase),
  };

  return nextFlows.map((flow) => (flow.id === updatedFlow.id ? updatedFlow : flow));
}

export function upsertProductCardFlowNodesForSections(
  flows: FlowDefinition[],
  sections: Array<{
    id: string;
    settings?: {
      canvas?: {
        blocks?: Array<{
          id: string;
          type: string;
          content?: Record<string, unknown>;
        }>;
      };
    };
  }>
) {
  return sections.reduce((nextFlows, section) => {
    const productCards = section.settings?.canvas?.blocks?.filter(
      (block) => block.type === 'product-card'
    );
    if (!productCards || productCards.length === 0) return nextFlows;
    return productCards.reduce(
      (currentFlows, block) =>
        upsertProductCardFlowNode(currentFlows, {
          sectionId: section.id,
          blockId: block.id,
          label:
            typeof block.content?.title === 'string'
              ? block.content.title
              : 'Product Card',
          sections,
        }),
      nextFlows
    );
  }, flows);
}
